/**
 * Custom element that controls when a hero/slide <video> starts loading.
 *
 * data-priority  "high" | "medium"
 *   high   — activate immediately once viewport check passes
 *   medium — activate via IntersectionObserver at 300px rootMargin
 *
 * data-media  "desktop" | "mobile" | (absent = both)
 *   Viewport guard. CSS display:none does NOT stop Chrome downloading video
 *   sources. Keeping src out of the DOM (data-src) is the only reliable fix.
 *
 * Why no `autoplay` in HTML?
 * Chrome pre-fetches bytes for `autoplay muted` elements even with
 * preload="none". Omitting autoplay and calling play() from JS prevents that.
 *
 * iOS Low Power Mode fallback
 * When video.play() rejects (iOS Low Power Mode blocks autoplay even for
 * muted+playsinline videos), a play button overlay appears over the poster.
 * A click from a real user gesture always succeeds on iOS.
 */

(function injectPlayButtonStyles() {
  if (document.getElementById('hvl-styles')) return;
  const style = document.createElement('style');
  style.id = 'hvl-styles';
  style.textContent = `
    .hvl-play-btn {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      cursor: pointer;
      z-index: 10;
      transition: background 0.2s ease, transform 0.2s ease;
      -webkit-tap-highlight-color: transparent;
    }
    .hvl-play-btn:hover,
    .hvl-play-btn:focus-visible {
      background: rgba(255, 255, 255, 0.32);
      transform: translate(-50%, -50%) scale(1.05);
    }
    .hvl-play-btn:focus-visible {
      outline: 1px solid rgba(255, 255, 255, 0.7);
      outline-offset: 2px;
    }
    .hvl-play-btn svg {
      width: 16px;
      height: 16px;
      fill: #fff;
      margin-left: 2px;
      pointer-events: none;
    }
    @media (prefers-reduced-motion: reduce) {
      .hvl-play-btn { transition: none; }
      .hvl-play-btn:hover,
      .hvl-play-btn:focus-visible { transform: translate(-50%, -50%); }
    }
  `;
  document.head.appendChild(style);
}());

const HVL_DEBUG = /[?&]hvl-debug\b/.test(window.location.search);

let hvlPanel = null;
function hvlPanelLine(text) {
  if (!hvlPanel) {
    hvlPanel = document.createElement('pre');
    hvlPanel.id = 'hvl-debug-panel';
    hvlPanel.style.cssText = `
      position: fixed; inset-inline: 0; bottom: 0; max-height: 40vh;
      margin: 0; padding: 8px; overflow-y: auto; overscroll-behavior: contain;
      background: rgba(0, 0, 0, 0.85); color: #0f0; font: 11px/1.4 monospace;
      white-space: pre-wrap; word-break: break-word; z-index: 2147483647;
      pointer-events: auto;
    `;
    document.addEventListener('DOMContentLoaded', () => document.body.appendChild(hvlPanel));
    if (document.body) document.body.appendChild(hvlPanel);
  }
  const line = document.createElement('div');
  line.textContent = `${new Date().toISOString().slice(11, 23)}  ${text}`;
  hvlPanel.appendChild(line);
  hvlPanel.scrollTop = hvlPanel.scrollHeight;
}

const hvlLog = (label, data) => {
  if (!HVL_DEBUG) return;
  console.log('[HVL]', label, data);
  let summary = label;
  if (data) {
    const parts = [];
    if (data.id !== undefined) parts.push(`id=${data.id}`);
    if (data.priority !== undefined) parts.push(`priority=${data.priority}`);
    if (data.media !== undefined) parts.push(`media=${data.media}`);
    if (data.matches !== undefined) parts.push(`matches=${data.matches}`);
    if (data.isIntersecting !== undefined) parts.push(`isIntersecting=${data.isIntersecting}`);
    if (data.width !== undefined) parts.push(`width=${data.width}`);
    if (data.video !== undefined) parts.push(`video=${data.video ? 'found' : 'MISSING'}`);
    if (data.isPromise !== undefined) parts.push(`promise=${data.isPromise}`);
    if (data.error !== undefined) parts.push(`error=${data.error}`);
    if (data.message !== undefined) parts.push(`msg=${data.message}`);
    if (data.alreadyShown !== undefined) parts.push(`alreadyShown=${data.alreadyShown}`);
    if (parts.length) summary += ` (${parts.join(', ')})`;
  }
  hvlPanelLine(summary);
};

class HeroVideoLoader extends HTMLElement {
  _label() {
    const video = this.querySelector('video');
    return (video && (video.dataset.testid || video.className)) || this.tagName;
  }

  connectedCallback() {
    const media = this.dataset.media;
    const matches = this._mediaMatches(media);
    hvlLog('connected', {
      id: this._label(),
      priority: this.dataset.priority,
      media,
      matches,
      width: window.innerWidth,
    });
    if (matches) {
      this._setup();
    } else {
      this._watchMedia(media);
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this._mq?.removeEventListener('change', this._mqListener);
    this._playBtn?.remove();
    this._playBtn = null;
  }

  _mediaMatches(media) {
    if (media === 'desktop') return window.matchMedia('(min-width: 750px)').matches;
    if (media === 'mobile') return window.matchMedia('(max-width: 749px)').matches;
    return true;
  }

  _watchMedia(media) {
    const query = media === 'mobile' ? '(max-width: 749px)' : '(min-width: 750px)';
    this._mq = window.matchMedia(query);
    this._mqListener = (e) => {
      if (!e.matches) return;
      this._mq.removeEventListener('change', this._mqListener);
      this._setup();
    };
    this._mq.addEventListener('change', this._mqListener);
  }

  _setup() {
    if (this.dataset.priority === 'high') {
      this._activate();
      return;
    }
    // Observe the <video>, not `this` — `<hero-video-loader>` is styled
    // `display: contents` so the inner <video> can participate directly in
    // the parent grid/layout. An element with `display: contents` generates
    // no box (getBoundingClientRect() is empty), so IntersectionObserver can
    // never compute an intersection for it and the callback never fires with
    // isIntersecting: true. Observing the <video> gives the observer real
    // geometry to work with.
    const video = this.querySelector('video');
    if (!video) return;
    hvlLog('observing video', { id: this._label() });
    this._observer = new IntersectionObserver(
      (entries) => {
        hvlLog('intersection', { id: this._label(), isIntersecting: entries[0].isIntersecting });
        if (entries[0].isIntersecting) {
          this._observer.disconnect();
          this._activate();
        }
      },
      { rootMargin: '300px 0px' }
    );
    this._observer.observe(video);
  }

  _activate() {
    const video = this.querySelector('video');
    const id = this._label();
    hvlLog('activate', { id, video });
    if (!video) return;

    for (const source of this.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
    }

    video.load();

    const playPromise = video.play();
    hvlLog('play() called', { id, isPromise: playPromise instanceof Promise });
    if (playPromise instanceof Promise) {
      playPromise.then(
        () => hvlLog('play() RESOLVED — autoplay allowed, no button needed', { id }),
        (err) => {
          hvlLog('play() REJECTED — showing button', { id, error: err && err.name, message: err && err.message });
          this._showPlayButton(video);
        }
      );
    }
  }

  _showPlayButton(video) {
    const id = this._label();
    hvlLog('showPlayButton appendChild', { id, alreadyShown: !!this._playBtn });
    if (this._playBtn) return;

    const btn = document.createElement('button');
    btn.className = 'hvl-play-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Play video');
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
        aria-hidden="true" focusable="false">
      <path d="M8 5v14l11-7z"/>
    </svg>`;

    btn.addEventListener('click', () => {
      btn.disabled = true;
      video.play().then(() => {
        btn.remove();
        this._playBtn = null;
      }).catch(() => {
        btn.disabled = false;
      });
    });

    this._playBtn = btn;
    this.appendChild(btn);
  }
}

if (!customElements.get('hero-video-loader')) {
  customElements.define('hero-video-loader', HeroVideoLoader);
}
